import {
  Dialog as MUIDialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import EaseAndConvenience from '../../../assets/icons/EaseAndConvenience';
import EnrollRemainingPolicies from '../../../assets/icons/EnrollRemainingPolicies';
import FasterCommunication from '../../../assets/icons/FasterCommunication';
import GoGreen from '../../../assets/icons/GoGreen';
import Button from '../../../components/styledComponents/Button';
import { RemoveButton } from '../../../components/styledComponents/IconButtons';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import theme from '../../../styles/theme';
import { setWithExpiry } from '../../../utils/helpers';

const PaperlessTitle = [
  'Go paperless to more easily manage your insurance policy',
  'Go paperless and enjoy faster communications',
  'Go paperless and help protect the environment',
  'Go paperless for all your policies',
];

const PaperlessText = [
  'Receive notifications by email. Then sign into MyPolicy to conveniently and securely view bills and insurance documents anytime, from virtually anywhere.',
  'Receive bill and insurance policy notifications more quickly by email when you sign up for paperless.',
  'It’s easy to reduce paper at home and help protect the environment when you go paperless. You’ll receive bill and insurance policy notifications by email.',
  'At least one of your policies is not enrolled in paperless. Reduce paper at home by going paperless for all your policies.',
];

interface PaperlessPreferencesDialogProps {
  open: boolean;
  setShowPaperlessPreferencesDialog: (isOpen: boolean) => void;
  contentSelection: number;
  setPaperlessDialogShown: (isOpen: boolean) => void;
}

const PaperlessPreferencesDialog = ({
  open,
  setShowPaperlessPreferencesDialog,
  contentSelection,
  setPaperlessDialogShown,
}: PaperlessPreferencesDialogProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const { track } = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      track('Targeted Promotion Displayed', {
        event_type: 'Automated System Process',
        contents: 'Reduce paper at home',
        location: 'Modal',
      });
    }
  }, [open]);

  const enrollInPaperlessPreferences = async (): Promise<void> => {
    track('Communication Preferences Accessed', {
      event_type: 'Option Selected',
      link: 'Set preferences',
      location: 'Modal',
    });
    track('Paperless Accepted', {
      event_type: 'Option Selected',
      location: 'Modal',
      link: 'Set Preferences',
    });
    navigate('/preferences');
    setShowPaperlessPreferencesDialog(false);
    setPaperlessDialogShown(true);
  };

  const declinePaperlessPreferences = (): void => {
    track('Paperless Declined', {
      event_type: 'Option Selected',
      location: 'Modal',
    });
    setWithExpiry('declinedPaperless', true, 60);
    setShowPaperlessPreferencesDialog(false);
    setPaperlessDialogShown(true);
  };

  const getPaperlessPreferencesIcon = () => {
    switch (contentSelection) {
      case 0:
        return (
          <EaseAndConvenience
            sx={{
              width: { mobile: 288, tablet: 480 },
              height: { mobile: 221, tablet: 366 },
            }}
          />
        );
      case 1:
        return (
          <FasterCommunication
            sx={{
              width: { mobile: 288, tablet: 480 },
              height: { mobile: 221, tablet: 367 },
            }}
          />
        );
      case 2:
        return (
          <GoGreen
            sx={{
              width: { mobile: 288, tablet: 480 },
              height: { mobile: 221, tablet: 367 },
            }}
          />
        );
      case 3:
        return (
          <EnrollRemainingPolicies
            sx={{
              width: { mobile: 288, tablet: 480 },
              height: { mobile: 221, tablet: 367 },
            }}
          />
        );
      default:
        return (
          <EaseAndConvenience
            sx={{
              width: { mobile: 288, tablet: 480 },
              height: { mobile: 221, tablet: 366 },
            }}
          />
        );
    }
  };

  return (
    <MUIDialog
      aria-labelledby="paperlessPreferencesLabel"
      data-testid="paperless-preferences-dialog"
      open={open}
      onClose={declinePaperlessPreferences}
      sx={{ backdropFilter: 'blur(5px)' }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          margin: 0,
          px: { mobile: 3, tablet: 4 },
          position: { mobile: 'fixed', tablet: 'relative' },
          width: { mobile: 1, tablet: 480 },
          height: 'auto',
          maxHeight: 1,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: 1,
          mt: { mobile: 2, tablet: 3 },
        }}
      >
        <RemoveButton
          aria-label="Dismiss paperless preferences modal"
          data-testid="close-paperless-dialog-button"
          onClick={declinePaperlessPreferences}
          autoFocus
        />
      </Box>
      <Stack direction="row" justifyContent="center">
        {getPaperlessPreferencesIcon()}
      </Stack>
      <DialogContent
        sx={{
          pt: { mobile: 2, tablet: 3 },
          pb: { mobile: 0, tablet: 3 },
          px: 0,
        }}
      >
        <Typography
          id="paperlessPreferencesLabel"
          sx={{
            fontSize: { tablet: 18 },
            fontWeight: 700,
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            mb: 1,
          }}
        >
          {PaperlessTitle[contentSelection]}
        </Typography>
        <Typography
          sx={{
            fontSize: { tablet: 18 },
            fontWeight: 400,
            fontFamily: 'Cabin',
            fontKerning: 'normal',
          }}
        >
          {PaperlessText[contentSelection]}
        </Typography>
        <Stack
          direction="column"
          spacing={isMobile ? 1 : 2}
          sx={{ mt: { mobile: 3, tablet: 5 }, mb: 3 }}
        >
          <Button
            fullWidth
            data-testid="not-now-paperless-button"
            variant="outlined"
            sx={{
              whiteSpace: 'nowrap',
              color: colors.csaaBlack,
              border: `solid 2px ${colors.csaaBlack}`,
              height: { mobile: 36, tablet: 42 },
            }}
            onClick={declinePaperlessPreferences}
          >
            Not now
          </Button>
          <Button
            role="link"
            fullWidth
            data-testid="go-paperless-button"
            variant="contained"
            sx={{
              whiteSpace: 'nowrap',
              height: { mobile: 36, tablet: 42 },
            }}
            onClick={enrollInPaperlessPreferences}
          >
            Set preferences
          </Button>
        </Stack>
      </DialogContent>
    </MUIDialog>
  );
};

export default PaperlessPreferencesDialog;
